/**
 * ----- API Models -----
 * Types and interfaces that mirror the backend API responses and requests.
 */

/**
 * Represents the status of a progress update
 *
 * @constant
 */
export const ProgressStatus = {
  Started: "started",
  Completed: "completed",
  Failed: "failed",
} as const;

/**
 * Represents the trigger option for the chat API
 * @constant
 */
export const TriggerOption = {
  ON: "on",
  OFF: "off",
  AUTO: "auto",
} as const;

/**
 * Represents the type of a message in the stream coming from the backend
 * @constant
 */
export const StreamMessageTypeValues = {
  Content: "content",
  Progress: "progress",
} as const;

/**
 * Represents the type of a source in the stream coming from the backend
 * @constant
 */
export const ContentTypeValues = {
  ScientificSource: "scientific_source",
  MateriomFormulation: "materiom_formulation",
} as const;

/**
 * Type of source either coming from the backend or selected by the users
 */
export type ContentType =
  | typeof ContentTypeValues.ScientificSource
  | typeof ContentTypeValues.MateriomFormulation;

/**
 * Represents the detailed information for a progress step
 * @interface ProgressDetail
 * @property {typeof ProgressStatus[keyof typeof ProgressStatus]} [status] - The status of the progress
 * @property {string} description - A descriptive message for the progress step
 */
export interface ProgressDetail {
  status?: typeof ProgressStatus[keyof typeof ProgressStatus];
  description: string;
}

/**
 * Represents a progress update
 * @interface ProgressUpdate
 * @property {number} step - The current step of the progress
 * @property {number} total_steps - The total number of steps
 * @property {string} step_name - The name of the step
 * @property {ProgressDetail} detail - Detailed information about the progress
 */
export interface ProgressUpdate {
  step_name: string;
  step_number: number | null;
  total_steps: number | null;
  detail: ProgressDetail;
}

/**
 * Represents a message in the stream
 * @interface StreamMessage
 * @property {string} type - The type of message
 * @property {Object} update - The data of the message
 */
export interface StreamMessage {
  type: typeof StreamMessageTypeValues[keyof typeof StreamMessageTypeValues];
  update:
    | ProgressUpdate
    | string
    | ScientificSourceContent[]
    | MateriomFormulationContent[];
}

/**
 * Represents a generic content object.
 * @interface Content
 * @property {ContentType} type - The type of the content.
 * @property {string} id - The ID of the content.
 */
export interface Content {
  type: ContentType;
  id: string;
}

/**
 * Represents scientific source content.
 * @interface ScientificSourceContent
 * @extends {Content}
 * @property {string} title - The title of the scientific source.
 * @property {string} abstract - The abstract of the scientific source.
 * @property {string} original_url - The original URL of the scientific source.
 * @property {number} year - The year of publication of the scientific source.
 * @property {string} venue - The venue where the scientific source was published.
 * @property {string} markdown_link - The markdown link to the scientific source.
 */
export interface ScientificSourceContent extends Content {
  title: string;
  abstract: string;
  url: string;
  year: number;
  venue: string;
  markdown_link: string;
}

/**
 * Represents a method in a recipe.
 * @interface Method
 * @property {string} type - The type of the method.
 * @property {string} description - The description of the method.
 */
export interface Method {
  type: string;
  description: string;
}

/**
 * Represents a property item in a material property.
 * @interface PropertyItem
 * @property {string} value - The value of the property item.
 * @property {string} unit - The unit of the property item.
 */
export interface PropertyItem {
  value: string;
  unit: string | null;
}

/**
 * Represents a material property in a recipe.
 * @interface MaterialProperty
 * @property {string} name - The name of the material property.
 * @property {string} value - The value of the material property.
 * @property {string[]} items - The items of the material property.
 */
export interface MaterialProperty {
  name: string;
  value: string;
  items: PropertyItem[];
}

/**
 * Represents commons recipe content.
 * @interface MateriomFormulationContent
 * @extends {Content}
 * @property {string} url - The URL of the recipe.
 * @property {string} name - The name of the recipe.
 * @property {string[]} ingredients - The ingredients of the recipe.
 * @property {number[]} ingredient_concentrations - The concentrations of the ingredients.
 * @property {string[]} ingredient_units - The units of the ingredients.
 * @property {string[]} author_ids - The author IDs of the recipe.
 */
export interface MateriomFormulationContent extends Content {
  url: string;
  name: string;
  ingredients: string[];
  ingredient_concentrations: number[];
  ingredient_units: string[];
  author_ids: string[];
  author_names: string[];
  author_last_names: string[] | null;
  contributor_ids: string[] | null;
  contributor_names: string[] | null;
  contributor_last_names: string[] | null;
  methods: Method[] | null;
  material_properties: MaterialProperty[] | null;
}

/**
 * Represents a user
 * @interface User
 * @property {string} _id - The ID of the user.
 * @property {string} name - The name of the user.
 * @property {string} last_name - The last name of the user.
 * @property {string} institute - The institute of the user.
 * @property {string} email - The email of the user.
 * @property {string} [image] - The image of the user.
 */
export interface User {
  _id: string;
  name: string;
  last_name: string;
  institute: string;
  image?: string;
  email_verified_at?: string | null;
}
/**
 * Represents the registration payload for user signup.
 * @interface RegistrationPayload
 * @property {string} name - The first name of the user
 * @property {string} last_name - The last name of the user
 * @property {string} email - The email address for the account
 * @property {string} password - The password for the account
 * @property {string} password_confirmation - The password confirmation
 * @property {boolean} commercial_consent - Whether the user consents to commercial communications
 */
export interface RegistrationPayload {
  name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  commercial_consent: boolean;
}

/**
 * Represents the registration response from the API.
 * @interface AuthenticationResponse
 * @property {string} token - The JWT authentication token
 * @property {Object} user - The registered user information
 * @property {string} user._id - The unique identifier of the user
 * @property {string} user.name - The first name of the user
 * @property {string} user.last_name - The last name of the user
 */
export interface AuthenticationResponse {
  token: string;
  user: User;
}
